/**
 * Run-layer types (SCOPE_STAGE2). The whole RunState is plain JSON —
 * serializable for Pause & Resume (A8) incl. all RNG counters.
 */
import type { BalanceConfig, Rarity } from '../config/balance'
import type { HexPos } from '../sim/hex'
import type { TeamId, EndReason } from '../sim/types'
import type { RngCounters } from './rngStreams'

export type Tier = 1 | 2 | 3

export interface OwnedChampion {
  uid: number
  champ: string // champion config key
  tier: Tier
  boughtRound: number
  buyPrice: number
  /** equipped weapon uids (hand validation in roster.ts); order = hand slots */
  weaponUids: number[]
  /** board position (absolute grid coords, own half) or null = bench */
  hex: HexPos | null
}

export interface OwnedWeapon {
  uid: number
  weapon: string // weapon config key
  tier: Tier
  boughtRound: number
  buyPrice: number
}

export interface Fracture {
  col: number
  row: number // absolute grid row (player backline rows)
  round: number // created in round (FIFO repair order)
  zone: number // column zone index (0=L,1=M,2=R)
}

export type FocusId = 'merge' | 'variety' | 'rarityChamps' | 'rarityItems' | 'gold'

export type ShopPath = 'character' | 'item'

export interface ShopSlot {
  kind: 'champion' | 'weapon' | 'passive'
  key: string // config key
  tier: Tier // weapons can appear at unlocked tiers; champions always 1 in shop
  rarity: Rarity
  price: number
  locked: boolean
  bought: boolean
}

/**
 * S1 v1.2: Bonus-Charakter des Item-Pfads — GRATIS nehmbar, fix pro RUNDE
 * (ein Draw beim ersten Item-Pfad-Eintritt der Runde; kein Refill nach Nahme,
 * kein Reroll-Einfluss, fokus-neutral gezogen).
 */
export interface BonusChamp {
  key: string
  round: number // Runde des Draws
  taken: boolean
}

export interface ShopState {
  visit: 1 | 2
  path: ShopPath | null // binaere Wahl; null = noch nicht gewaehlt
  slots: ShopSlot[]
  paidRerollsUsed: number // Index in der Kosten-Sequenz (Free Rerolls zaehlen nicht)
  freeRerolls: number
  weaponRatio: number // Item-Pfad: Anzahl Waffen-Slots (0..itemSlotsTotal)
  repairsUsed: number
}

export interface CrossBonus {
  kind: 'item' | 'character' // fuer welchen Pfad die Free Rerolls gelten
  amount: number
  grantedRound: number // Vergabe-Runde — einloesbar erst in einer KUENFTIGEN Runde (docs/02)
  expiresAfterRound: number // gilt bis einschliesslich dieser Runde
}

export type RunPhase = 'focusPick' | 'shop' | 'result' | 'ended'

export interface CombatOutcome {
  round: number
  attempt: 1 | 2
  seed: number
  ghostSeed: number
  playerWon: boolean
  autoLoss: boolean // S2: leeres Board -> kampfloser Loss (Bodennetz)
  winner: TeamId | 'draw'
  reason: EndReason
  enemyPowerBp: number
  hpLoss: number // 0 bei Win
  stageBase: number
  outcomeMod: number
  fractureSpawned: boolean
}

export interface RunState {
  version: number
  masterSeed: number
  round: number
  phase: RunPhase
  attempt: 1 | 2
  lossesThisRound: number
  wonThisRound: boolean // Clean Win in Attempt 1 (fuer Win-Bonus der Folgerunde)
  hp: number
  gold: number
  nextUid: number
  champions: OwnedChampion[] // Board (hex != null) + Bench (hex == null)
  weapons: OwnedWeapon[] // alle; "im Storage" = von keinem Champion referenziert
  passives: string[] // gekaufte Passives (permanent, teamweit — wirken sofort)
  fractures: Fracture[]
  lastFractureZone: number // -1 = noch keine
  focus: FocusId | null
  bonusChamp: BonusChamp | null
  shop: ShopState
  crossBonuses: CrossBonus[]
  recoveryTokens: number // Free Rerolls fuer den naechsten Shop (aus 2-Loss-Recovery)
  lowHpRerollRound: number // letzte Runde mit vergebenem Low-HP-Free-Reroll (max 1x/Runde)
  /** Ghost-Seed der laufenden Runde — Attempt 2 kaempft gegen DASSELBE Team (A-CC37) */
  roundGhostSeed?: number
  /** hoechstes selbst gecraftetes Tier pro Waffen-Key (Shop-Unlock, docs/02) */
  craftedWeaponTiers: Record<string, number>
  rngCounters: RngCounters
  lastCombat: CombatOutcome | null
  stats: {
    losses: number // "Deaths" im Run-Ende-Screen (docs/06 Tiebreaker)
    attempts: number
    cleanWins: number
  }
  /** Config-Snapshot des Runs — Debug-Aenderungen wirken auf NEUE Runs */
  config: BalanceConfig
}

export const RUN_STATE_VERSION = 2 // v2: BonusChamp-Modell (S1), Auto-Loss (S2), Stage-Odds (S3)
